import { createElement } from 'react'
import type { ComponentPropsWithoutRef, JSX } from 'react'
import { createVariants } from 'shared-ui/lib'
import { isNonNil } from 'utils/object'

/* -------------------------------- INTERNAL -------------------------------- */

/** Fixed-tag element whose base classes merge (via tailwind-variants) with any passed className. */
const styled = <Tag extends keyof JSX.IntrinsicElements>(tag: Tag, base: string) => {
  const classes = createVariants({ base })
  const Styled = ({ className, ...props }: ComponentPropsWithoutRef<Tag>) =>
    createElement(tag, { className: classes({ className }), ...props })
  Styled.displayName = `InfoCard(${tag})`
  return Styled
}

const Image = (props: { src: string; caption?: string }) => (
  <figure className="mx-auto flex w-fit max-w-full flex-col justify-center">
    <img src={props.src} alt="" className="self-center" />
    {isNonNil(props.caption) && (
      <figcaption className="pan-detail mt-4 text-center text-gray-500">{props.caption}</figcaption>
    )}
  </figure>
)

const wrapperBase =
  'mx-auto flex flex-col gap-8 rounded-lg border border-gray-300 bg-white p-6 md:p-12 lg:p-12'
const contentBase = 'grow'
const titleBase = 'pan-title1 mb-4 lg:mb-6'
const descriptionBase = 'mb-4 md:mb-12'
const buttonGroupBase = 'mt-8 lg:mt-12 flex flex-col gap-4'

/* --------------------------------- EXPORTS -------------------------------- */

/**
 * Vertical InfoCard
 */
export const VCard = {
  Wrapper: styled('div', `${wrapperBase} lg:flex-col lg:gap-8 w-full md:w-2/3 lg:w-5/12 xl:w-1/3`),
  Content: styled('div', contentBase),
  Title: styled('h2', `${titleBase} text-center`),
  Description: styled('p', `${descriptionBase} text-center`),
  ButtonGroup: styled('div', `${buttonGroupBase} md:flex-col items-stretch`),
  Image
}

/**
 * Horizontal InfoCard
 */
export const HCard = {
  Wrapper: styled('div', `${wrapperBase} lg:flex-row-reverse lg:gap-24`),
  Content: styled('div', contentBase),
  Title: styled('h2', titleBase),
  Description: styled('p', descriptionBase),
  ButtonGroup: styled('div', `${buttonGroupBase} md:flex-row md:mt-6`),
  Image
}
